import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { KeyResultInput, ObjectiveInput } from './types';

export const okrKeys = {
  objectives: ['okr', 'objectives'] as const,
  list: (p: api.ObjectiveListParams) => [...okrKeys.objectives, 'list', p] as const,
};

export function useObjectives(params: api.ObjectiveListParams = {}) {
  return useQuery({
    queryKey: okrKeys.list(params),
    queryFn: () => api.listObjectives(params),
    placeholderData: keepPreviousData,
  });
}

export function useCreateObjective() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ObjectiveInput) => api.createObjective(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: okrKeys.objectives });
      toast.success('Objective created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateObjective() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<ObjectiveInput> }) =>
      api.updateObjective(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: okrKeys.objectives }),
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteObjective() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteObjective(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: okrKeys.objectives });
      toast.success('Objective deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useCreateKeyResult() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: KeyResultInput) => api.createKeyResult(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: okrKeys.objectives }),
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useUpdateKeyResult() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<KeyResultInput> }) =>
      api.updateKeyResult(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: okrKeys.objectives }),
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useDeleteKeyResult() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteKeyResult(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: okrKeys.objectives }),
    onError: (err: ApiError) => toast.error(err.message),
  });
}
